import _ from "lodash";
import update from "immutability-helper";
import client from "lib/apolloClient/client";
import {
  createSubjectMutation,
  updateSubjectMutation,
  deleteSubjectMutation,
  createSubjectMutationV2,
  updateSubjectMutationV2,
} from "SubjectsSetup/modules/Mutations";
import { getOrganizationSubjectGroupsQuery } from "Administrator/routes/MYPAcademicSetup/routes/SubjectGroupsAndSubjects/modules/Queries.js";
import { getOrganizationSubjectsBasicDetailsQuery } from "Administrator/routes/AcademicSetup/routes/SubjectsSetup/modules/Queries.js";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import { getOrganizationSubjectsQuery } from "MultiLevelNodeEditor/modules/MultiLevelNodeEditorQueries";
import { getUserCourseTagsFilters } from "Courses/modules/utils";
import {
  CURRICULUM_TYPE_MYP,
  CURRICULUM_TYPE_DP,
} from "Constants/stringConstants";
import { getDPSubjectGroupsQuery } from "Administrator/routes/DPAcademicSetup/routes/DPSubjects/modules/DPSubjectsQueries";
import { dispatchError } from "Utils";
import { getOrganizationId } from "modules/CommonSelectors";
// Consts
export const NAME = "subjectsSetup";
export const CHANGE_SEARCH_TERM = "CHANGE_SEARCH_TERM" + " " + NAME;
export const INIT_SEARCH_TERM = "INIT_SEARCH_TERM" + " " + NAME;
export const TOGGLE_IS_LOADING = "TOGGLE_IS_LOADING" + " " + NAME;

export const toggleIsLoading = data => {
  return {
    type: TOGGLE_IS_LOADING,
    data,
  };
};

const getRefetchQuery = ({ curriculumType, showNewSubjectsScreen }) => {
  switch (curriculumType) {
    case CURRICULUM_TYPE_MYP:
      return getOrganizationSubjectGroupsQuery;
    case CURRICULUM_TYPE_DP:
      return getDPSubjectGroupsQuery;
    default:
      return showNewSubjectsScreen
        ? getOrganizationSubjectsBasicDetailsQuery
        : getOrganizationSubjectsQuery;
  }
};

export const createSubjectMutationFn = ({
  input,
  filters,
  selectedRegions,
  curriculumType,
  showNewSubjectsScreen,
  extraRefetchQueryProps = {},
  t,
}) => {
  return async (dispatch, getState) => {
    dispatch(toggleIsLoading(true));
    const organizationId = getState().login.userInfo.org_id;
    const userTags = _.get(getState(), "login.userInfo.tags", []);
    const filteredTags = _.isEmpty(selectedRegions)
      ? getUserCourseTagsFilters({ userTags })
      : { tags: selectedRegions };

    const inputWithTagFilters = { ...input, ...filteredTags };

    const query = getRefetchQuery({ curriculumType, showNewSubjectsScreen });

    try {
      const res = await client.mutate({
        mutation: createSubjectMutation,
        variables: inputWithTagFilters,
        awaitRefetchQueries: true,
        refetchQueries: [
          {
            query,
            variables: {
              id: organizationId,
              filters,
              ...extraRefetchQueryProps,
            },
          },
        ],
      });
      dispatch(toggleIsLoading(false));
      dispatch(
        setToastMsg({
          type: "success",
          msg: "toastMsgs:action_successfully",
          locale_params: [
            { key: "action", value: "toastMsgs:added", isPlainText: false },
            { key: "label", value: "common:subject", isPlainText: false },
          ],
        })
      );
      return res;
    } catch (err) {
      dispatch(toggleIsLoading(false));
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
      return err;
    }
  };
};

export const createSubjectMutationFnV2 = ({
  input,
  filters,
  selectedRegions,
  curriculumType,
  showNewSubjectsScreen,
  extraRefetchQueryProps = {},
  t,
}) => {
  return async (dispatch, getState) => {
    dispatch(toggleIsLoading(true));
    const organizationId = getState().login.userInfo.org_id;
    const userTags = _.get(getState(), "login.userInfo.tags", []);
    const filteredTags = _.isEmpty(selectedRegions)
      ? getUserCourseTagsFilters({ userTags })
      : { tags: selectedRegions };

    const inputWithTagFilters = { ...input, ...filteredTags };

    const query = getRefetchQuery({ curriculumType, showNewSubjectsScreen });

    try {
      const response = await client.mutate({
        mutation: createSubjectMutationV2,
        variables: inputWithTagFilters,
        awaitRefetchQueries: true,
        refetchQueries: [
          {
            query,
            variables: {
              id: organizationId,
              filters,
              subjectCourseFilter: {
                archivalState: "ALL",
              },
              ...extraRefetchQueryProps,
            },
          },
        ],
      });
      const { isSuccess, warning } =
        _.get(response, "data.platform.createSubjectV2") || {};
      if (isSuccess) {
        dispatch(
          setToastMsg({
            type: "success",
            msg: "toastMsgs:action_successfully",
            locale_params: [
              { key: "action", value: "toastMsgs:added", isPlainText: false },
              { key: "label", value: "common:subject", isPlainText: false },
            ],
          })
        );
      }
      dispatch(toggleIsLoading(false));
      return { isSuccess, warning };
    } catch (err) {
      dispatch(toggleIsLoading(false));
      dispatchError({ dispatch, e: err });
      return err;
    }
  };
};

export const updateSubjectMutationFn = ({ input, t }) => {
  return (dispatch, getState) => {
    try {
      client.mutate({
        mutation: updateSubjectMutation,
        variables: {
          ...input,
        },
      });
      dispatch(
        setToastMsg({
          type: "success",
          msg: "toastMsgs:action_successfully",
          locale_params: [
            { key: "action", value: "toastMsgs:updated", isPlainText: false },
            { key: "label", value: "common:subject", isPlainText: false },
          ],
        })
      );
    } catch (err) {
      console.error(err);
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
    }
  };
};

export const updateSubjectMutationFnV2 = ({
  input,
  subjectGroupFilters,
  subjectFilters,
  flatSubjectVariantFilters,
  coursesArchivalState,
}) => {
  return async (dispatch, getState) => {
    const orgId = getOrganizationId(getState());

    const refetchQueries = [];

    // if subject is interdisciplinary, refetch feed
    if (_.has(input, "subjectGroup")) {
      refetchQueries.push({
        query: getDPSubjectGroupsQuery,
        variables: {
          id: orgId,
          filters: subjectGroupFilters,
          subjectFilters,
          flatSubjectVariantFilters,
          flatVariantsCourseFilters: coursesArchivalState,
          subjectCourseFilter: coursesArchivalState,
        },
      });
    }

    try {
      const response = await client.mutate({
        mutation: updateSubjectMutationV2,
        variables: {
          ...input,
        },
        awaitRefetchQueries: true,
        refetchQueries,
      });
      const { isSuccess, warning } =
        _.get(response, "data.platform.updateSubjectV2") || {};
      if (isSuccess) {
        dispatch(
          setToastMsg({
            type: "success",
            msg: "toastMsgs:action_successfully",
            locale_params: [
              { key: "action", value: "toastMsgs:updated", isPlainText: false },
              { key: "label", value: "common:subject", isPlainText: false },
            ],
          })
        );
      }
      return { isSuccess, warning };
    } catch (err) {
      console.error(err);
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
    }
  };
};

export const deleteSubjectMutationFn = ({
  id,
  filters,
  curriculumType,
  extraRefetchQueryProps = {},
  subjectName,
  t,
}) => {
  return async (dispatch, getState) => {
    const organizationId = getState().login.userInfo.org_id;
    const query = getRefetchQuery({ curriculumType });

    try {
      await client.mutate({
        mutation: deleteSubjectMutation,
        variables: {
          id,
        },
        awaitRefetchQueries: true,
        refetchQueries: [
          {
            query,
            variables: {
              id: organizationId,
              filters,
              ...extraRefetchQueryProps,
            },
          },
        ],
      });
      dispatch(
        setToastMsg({
          type: "success",
          msg: "toastMsgs:action_successfully",
          locale_params: [
            { key: "action", value: "toastMsgs:deleted", isPlainText: false },
            { key: "label", value: "common:subject", isPlainText: false },
          ],
        })
      );
    } catch (e) {
      console.error(e);
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
    }
  };
};

export const archiveSubjectMutationFn = ({
  input,
  t,
  filters,
  curriculumType,
  extraRefetchQueryProps = {},
}) => {
  return async (dispatch, getState) => {
    const organizationId = getState().login.userInfo.org_id;
    const query = getRefetchQuery({ curriculumType });

    try {
      await client.mutate({
        mutation: updateSubjectMutation,
        variables: {
          ...input,
        },
        awaitRefetchQueries: true,
        refetchQueries: [
          {
            query,
            variables: {
              id: organizationId,
              filters,
              ...extraRefetchQueryProps,
            },
          },
        ],
      });
      dispatch(
        setToastMsg({
          type: "success",
          msg: "toastMsgs:action_successfully",
          locale_params: [
            {
              key: "action",
              value: !input.isArchived ? "common:restored" : "common:archived",
              isPlainText: false,
            },
            { key: "label", value: "common:subject", isPlainText: false },
          ],
        })
      );
    } catch (error) {
      dispatchError({ dispatch, error });
    }
  };
};

// Reducer Handlers
const REDUCER_HANDLERS = {
  [CHANGE_SEARCH_TERM]: (state, action) => {
    return update(state, { searchText: { $set: action.payload } });
  },
  [INIT_SEARCH_TERM]: (state, action) => {
    return update(state, { searchText: { $set: initialState.searchText } });
  },
  [TOGGLE_IS_LOADING]: (state, action) => {
    return update(state, { isLoading: { $set: action.data } });
  },
};

// Initial State and export Reducer

const initialState = {
  isLoading: false,
};

export default function myReducer(state = initialState, action) {
  const handler = REDUCER_HANDLERS[action.type];
  return handler ? handler(state, action) : state;
}
